import React from "react";
import styled from "styled-components/macro";
import {
    Box, Grid, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip,
} from "@material-ui/core";
import {withStyles} from "@material-ui/core/styles";
import {LEVEL_TO_STATUS, STATUS_COLORS, STATUS_ORDER, STATUS_ORDER_INDEX_TO_COLOR_KEY} from "../../../../utils/constants";
import {statusToDisplay} from "../hooks/useBarChartData";

const categoryBgGrey = "#faf7f6";
const headerBg = "#111";

const LegendSquare = styled(Box)`
    width: 110px;
    height: 32px;
    background: ${props => props.color};
    color: ${props => (props.textColor ? props.textColor : "#fff")};
    font-weight: 700;
    font-size: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    @media (min-width: 768px) and (max-width: 1024px) {
        font-size: 9px;
        width: 125px;
    }
    @media (max-width: 768px) {
        font-size: 8px;
    }
`;

// Styled cells
const StatusCell = styled(TableCell)`
    && {
        background: ${({filled, color, isHeader, isGreyed}) =>
    filled ? color
        : isHeader ? headerBg
            : isGreyed ? categoryBgGrey
                : "#fff"};
        color: ${({filled, isHeader}) => (filled || isHeader ? "#fff" : "#222")};
        font-weight: ${({filled, isHeader}) => (filled || isHeader ? 700 : 400)};
        font-size: 12px;
        text-align: center;
        border: 1px solid #ccc;
        padding: 20px;
        min-width: 200px;
        height: 38px;
    }
`;

const CategoryCell = styled(TableCell)`
    && {
        background: ${({header, alt}) => header ? headerBg : (alt ? categoryBgGrey : "#fff")};
        color: ${({header}) => (header ? "#fff" : "#222")};
        font-weight: 700;
        font-size: 12px;
        text-align: center;
        border: 1px solid #ccc;
        vertical-align: middle;
        min-width: ${({header}) => (header ? "160px" : "120px")};
        white-space: pre-line;
        padding: ${({header}) => (header ? "0 12px" : "0 8px")};
    }
`;

const LabelCell = styled(TableCell)`
    && {
        background: ${({header, alt}) => header ? headerBg : (alt ? categoryBgGrey : "#fff")};
        color: ${({header}) => (header ? "#fff" : "#222")};
        font-weight: 700;
        font-size: 11px;
        border: 1px solid #ccc;
        min-width: 200px;
        text-align: center;
        padding-left: 10px;
        padding-right: 0;
        white-space: pre-line;
    }
`;

const TablePaper = styled(Paper)`
    padding: 0;
    background: #fafbfc;
    max-width: 750px;
    min-width: 750px;
    min-height: 420px;
    margin-left: 32px;
    box-shadow: none;
`;

const StyledTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: "#233044",
        color: "#fff",
        boxShadow: "0 4px 12px #0003",
        fontSize: 14,
        fontWeight: 400,
        padding: 12,
        borderRadius: 8,
        maxWidth: 300,
    },
    arrow: {
        color: "#233044",
    },
}))(Tooltip);

function OverallScoreTab({ overall }) {
    return (
        <Box px={{ xs: 1, md: 4 }} py={5} width="100%">
            <Grid container spacing={1}>
                <Grid item xs={12} md={12}>
                    <Box display="flex" alignItems="center" mb={7} pl={4} pr={2}>
                        <Box width="100%">
                            <Typography variant="h4" style={{ fontWeight: 700, textAlign:'center', marginBottom: 4 }}>
                                Check Your School’s Performance
                            </Typography>
                            <Typography variant="body1" style={{ fontSize: 17,textAlign:'left', color: "#222", marginBottom: 16 }}>
                                You can review your school's overall performance compared to national and global benchmarks.
                            </Typography>
                        </Box>
                    </Box>
                    <Box display="flex" pr={7} mb={10} mt={7} justifyContent={'center'}>
                        {STATUS_ORDER.map((key, index) => {
                            const colorKey = !key || key === ""
                                ? STATUS_ORDER_INDEX_TO_COLOR_KEY[index] 
                                : key;
                            const color=STATUS_COLORS[colorKey];
                            return (
                                <LegendSquare
                                    key={index}
                                    color={color}
                                    textColor={colorKey === "active" ? "#222" : "#fff"}
                                    style={{ margin: 0, borderRadius: 0 }}
                                >
                                    {key ? statusToDisplay(key) : ""}
                                </LegendSquare>
                            );
                        })}
                    </Box>
                </Grid>
                <Grid item xs={12} md={12} >
                        <TableContainer style={{ maxWidth: "100%", overflowX: "auto" }}>
                            <Table>
                                <TableBody>
                                    {(!overall || overall.length === 0) ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" p={20}>
                                                No data available.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                    (() => {
                                        let globalRow = 0;
                                        const rows = [];
                                        overall.forEach((section, idx) => {
                                            section.rows.forEach((row, rIdx) => {
                                                globalRow += 1;
                                                const isTop3 = globalRow <= 3;
                                                rows.push(
                                                    <TableRow key={`${section.category}-${row.label}`}>
                                                        {rIdx === 0 && (
                                                            section.description ? (
                                                                <StyledTooltip
                                                                    title={section.description}
                                                                    placement="right"
                                                                    arrow
                                                                    enterTouchDelay={0}
                                                                    PopperProps={{
                                                                        modifiers: [
                                                                            {
                                                                                name: "offset",
                                                                                options: {
                                                                                    offset: [8, 0],
                                                                                },
                                                                            },
                                                                        ],
                                                                    }}
                                                                >
                                                                    <CategoryCell
                                                                        rowSpan={3}
                                                                        alt={section.greyed}
                                                                        style={isTop3 ? { background: "#111", color: "#fff" } : undefined}
                                                                    >
                                                                        <span style={{ display: "block", width: "100%", height: "100%" }}>
                                                                            {section.category}
                                                                        </span>
                                                                    </CategoryCell>
                                                                </StyledTooltip>
                                                            ) : (
                                                                <CategoryCell
                                                                    rowSpan={3}
                                                                    alt={section.greyed}
                                                                    style={isTop3 ? { background: "#111", color: "#fff" } : undefined}
                                                                >
                                                                    {section.category}
                                                                </CategoryCell>
                                                            )
                                                        )}
                                                        <LabelCell alt={section.greyed}                                                                 // you can add any extra styling for isTop3 here if you want
                                                                   style={isTop3 ? { background: "#111", color: "#fff" } : undefined}
                                                        >
                                                            {row.label}
                                                        </LabelCell>
                                                        {section.rubricNames.map((rubricName, colIdx) => {
                                                            // filled: should be true only if this is the highlight col
                                                            const isFilled = row.highlightCol === colIdx;
                                                            const statusColor = STATUS_COLORS[Object.keys(STATUS_COLORS)[colIdx]];
                                                            return (
                                                                <StatusCell
                                                                    key={rubricName}
                                                                    filled={isFilled}
                                                                    color={isFilled ? statusColor : undefined}
                                                                    isGreyed={section.greyed}
                                                                    // Only force black bg for isTop3 if not filled
                                                                    style={
                                                                        isTop3 && !isFilled
                                                                            ? { background: "#111", color: "#fff" }
                                                                            : undefined
                                                                    }
                                                                >
                                                                    {rubricName}
                                                                </StatusCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                );
                                            });
                                        });
                                        return rows;
                                    })()
                                        )}
                                </TableBody>
                            </Table>

                        </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
}

export default OverallScoreTab;
